// ===========================================
// TABLE LIFECYCLE REGISTRY
// ===========================================
// Centralized management of table join/leave lifecycle.
// Ensures consistent behavior for:
//   - Fresh JOIN from lobby
//   - RETURN from background (tab visible)
//   - LEAVE (navigate away)
//   - TAB_HIDDEN (background)
//
// Architecture Philosophy:
// "When backgrounded, go dormant. When foregrounded, resync fresh."
// Both JOIN and RETURN start from an EMPTY canvas and render from server state.
// ===========================================

import { create } from 'zustand';

// ===========================================
// TYPES
// ===========================================

export type ExitReason = 'navigate' | 'background';

export interface TableLifecycleState {
  // ----- State -----
  /** Is the table currently active (visible + connected) */
  isActive: boolean;
  /** Is the table backgrounded (tab hidden, waiting for return) */
  isDormant: boolean;
  /** Current table ID (null if not at a table) */
  currentTableId: string | null;
  /** Timestamp when dormant mode started (for diagnostics) */
  dormantSince: number | null;
  /** Delayed disconnect timer ID */
  disconnectTimerId: ReturnType<typeof setTimeout> | null;

  // ----- Callbacks (set by PixiTablePage) -----
  /** Callback to clear all Pixi visuals */
  onClearVisuals: (() => void) | null;
  /** Callback to clear animation queue */
  onClearAnimationQueue: (() => void) | null;
  /** Callback to disconnect socket */
  onDisconnectSocket: (() => void) | null;
  /** Callback to connect socket and join table */
  onConnectAndJoin: ((tableId: string) => void) | null;
  /** Callback to request table state (resync) */
  onRequestTableState: ((tableId: string) => void) | null;

  // ----- Actions -----
  /** Register callbacks from PixiTablePage */
  registerCallbacks: (callbacks: {
    onClearVisuals: () => void;
    onClearAnimationQueue: () => void;
    onDisconnectSocket: () => void;
    onConnectAndJoin: (tableId: string) => void;
    onRequestTableState: (tableId: string) => void;
  }) => void;

  /** Unregister callbacks (on unmount) */
  unregisterCallbacks: () => void;

  /**
   * Enter a table - called on both fresh JOIN and RETURN from background.
   * Clears canvas to empty state, connects socket, requests fresh state from server.
   */
  enterTable: (tableId: string) => void;

  /**
   * Exit a table - called on both LEAVE (navigate) and TAB_HIDDEN (background).
   * Clears all visuals immediately. Socket disconnect is:
   *   - Immediate for 'navigate'
   *   - Delayed (3s) for 'background' to avoid churn from brief tab switches
   */
  exitTable: (reason: ExitReason) => void;

  /** Cancel any pending disconnect (called if user returns quickly) */
  cancelPendingDisconnect: () => void;

  /** Mark as no longer dormant (called when tab becomes visible) */
  wake: () => void;

  /** Reset all state (called on full cleanup) */
  reset: () => void;
}

// ===========================================
// CONSTANTS
// ===========================================

/** Delay before disconnecting socket when backgrounded (prevents churn from brief tab switches) */
const BACKGROUND_DISCONNECT_DELAY_MS = 3000;

// ===========================================
// STORE
// ===========================================

export const useTableLifecycleRegistry = create<TableLifecycleState>((set, get) => ({
  // ----- Initial State -----
  isActive: false,
  isDormant: false,
  currentTableId: null,
  dormantSince: null,
  disconnectTimerId: null,

  // ----- Callbacks -----
  onClearVisuals: null,
  onClearAnimationQueue: null,
  onDisconnectSocket: null,
  onConnectAndJoin: null,
  onRequestTableState: null,

  // ----- Actions -----

  registerCallbacks: (callbacks) => {
    console.log('[TableLifecycle] Callbacks registered');
    set({
      onClearVisuals: callbacks.onClearVisuals,
      onClearAnimationQueue: callbacks.onClearAnimationQueue,
      onDisconnectSocket: callbacks.onDisconnectSocket,
      onConnectAndJoin: callbacks.onConnectAndJoin,
      onRequestTableState: callbacks.onRequestTableState,
    });
  },

  unregisterCallbacks: () => {
    console.log('[TableLifecycle] Callbacks unregistered');
    // Cancel any pending disconnect before unregistering
    get().cancelPendingDisconnect();
    set({
      onClearVisuals: null,
      onClearAnimationQueue: null,
      onDisconnectSocket: null,
      onConnectAndJoin: null,
      onRequestTableState: null,
    });
  },

  enterTable: (tableId: string) => {
    const state = get();
    console.log('[TableLifecycle] enterTable:', tableId, {
      wasActive: state.isActive,
      wasDormant: state.isDormant,
      previousTableId: state.currentTableId,
    });

    // Cancel any pending disconnect from a previous background
    state.cancelPendingDisconnect();

    // 1. Clear all visuals (start from EMPTY canvas - same as fresh JOIN)
    if (state.onClearVisuals) {
      console.log('[TableLifecycle] Clearing visuals');
      state.onClearVisuals();
    }

    // 2. Clear animation queue (abort pending animations)
    if (state.onClearAnimationQueue) {
      console.log('[TableLifecycle] Clearing animation queue');
      state.onClearAnimationQueue();
    }

    // 3. Connect socket and join table
    // IMPORTANT: Always use onConnectAndJoin, even when returning to the same table.
    // The socket may have been disconnected during dormant mode (after 3s delay).
    // onConnectAndJoin handles reconnection gracefully - it will only connect if needed.
    if (state.isDormant && state.currentTableId === tableId) {
      console.log('[TableLifecycle] Returning to same table - reconnecting and rejoining');
    } else {
      console.log('[TableLifecycle] Fresh join - connecting and joining');
    }
    if (state.onConnectAndJoin) {
      state.onConnectAndJoin(tableId);
    }

    // 4. Update state
    set({
      isActive: true,
      isDormant: false,
      currentTableId: tableId,
      dormantSince: null,
    });

    // 5. Show notification permission prompt (for future background notifications)
    if ('Notification' in window && Notification.permission === 'default') {
      // Don't await - let it happen in background
      Notification.requestPermission().then((permission) => {
        console.log('[TableLifecycle] Notification permission:', permission);
      });
    }
  },

  exitTable: (reason: ExitReason) => {
    const state = get();
    console.log('[TableLifecycle] exitTable:', reason, {
      isActive: state.isActive,
      tableId: state.currentTableId,
    });

    if (!state.isActive && !state.isDormant) {
      console.log('[TableLifecycle] Already exited, skipping');
      return;
    }

    // 1. Clear all visuals IMMEDIATELY (regardless of reason)
    if (state.onClearVisuals) {
      console.log('[TableLifecycle] Clearing visuals');
      state.onClearVisuals();
    }

    // 2. Clear animation queue IMMEDIATELY
    if (state.onClearAnimationQueue) {
      console.log('[TableLifecycle] Clearing animation queue');
      state.onClearAnimationQueue();
    }

    // 3. Handle socket based on reason
    if (reason === 'navigate') {
      // NAVIGATE: Disconnect immediately
      console.log('[TableLifecycle] Navigate - disconnecting socket immediately');
      if (state.onDisconnectSocket) {
        state.onDisconnectSocket();
      }
      set({
        isActive: false,
        isDormant: false,
        currentTableId: null,
        dormantSince: null,
      });
    } else {
      // BACKGROUND: Delayed disconnect + show notification
      console.log('[TableLifecycle] Background - entering dormant mode');

      // Show immediate notification
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification('BUSTED! Blackjack', {
            body: 'You have left the table. Your game will be terminated in 3 rounds.',
            icon: '/favicon.svg',
            tag: 'table-exit-warning',
          });
        } catch (err) {
          // Notifications may fail in some contexts (e.g., incognito)
          console.warn('[TableLifecycle] Failed to show notification:', err);
        }
      }

      // Schedule delayed disconnect
      const timerId = setTimeout(() => {
        console.log('[TableLifecycle] Delayed disconnect firing');
        const currentState = get();
        if (currentState.isDormant && currentState.onDisconnectSocket) {
          currentState.onDisconnectSocket();
        }
        set({ disconnectTimerId: null });
      }, BACKGROUND_DISCONNECT_DELAY_MS);

      set({
        isActive: false,
        isDormant: true,
        dormantSince: Date.now(),
        disconnectTimerId: timerId,
      });
    }
  },

  cancelPendingDisconnect: () => {
    const state = get();
    if (state.disconnectTimerId) {
      console.log('[TableLifecycle] Cancelling pending disconnect');
      clearTimeout(state.disconnectTimerId);
      set({ disconnectTimerId: null });
    }
  },

  wake: () => {
    const state = get();
    if (!state.isDormant) {
      return;
    }

    const dormantDuration = state.dormantSince ? Date.now() - state.dormantSince : 0;
    console.log('[TableLifecycle] Waking from dormant mode after', dormantDuration, 'ms');

    // Cancel pending disconnect - we're back
    state.cancelPendingDisconnect();

    // Note: Don't set isDormant=false here - enterTable() will do that
    // This just cancels the disconnect timer
  },

  reset: () => {
    const state = get();
    state.cancelPendingDisconnect();
    console.log('[TableLifecycle] Full reset');
    set({
      isActive: false,
      isDormant: false,
      currentTableId: null,
      dormantSince: null,
      disconnectTimerId: null,
    });
  },
}));

// ===========================================
// CONVENIENCE HOOKS
// ===========================================

/** Check if currently dormant (for useGameEvents to skip processing) */
export function isDormant(): boolean {
  return useTableLifecycleRegistry.getState().isDormant;
}

/** Check if currently active */
export function isActive(): boolean {
  return useTableLifecycleRegistry.getState().isActive;
}

/** Get current table ID */
export function getCurrentTableId(): string | null {
  return useTableLifecycleRegistry.getState().currentTableId;
}
